import clsx from 'clsx';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { ComponentProps, JSX } from 'react';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import type { FindingWithLayoutInfo } from 'ts/commons/code/FindingsLayout';
import { Links } from 'ts/commons/links/Links';
import { Markdown } from 'ts/commons/markdown/Markdown';
import { PathUtils } from 'ts/commons/PathUtils';
import { Icon } from 'ts/components/Icon';
import { Popup } from 'ts/components/Popup';
import { SiblingComparisonLink } from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabContent';
import type { FindingLikeType } from 'ts/perspectives/findings/FindingLikeType';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import { FindingLocationLink } from 'ts/perspectives/findings/list/FindingsTable.columns';
import type { ElementLocationSubtype } from 'typedefs/ElementLocationSubtype';
import { ETrafficLightColor } from 'typedefs/ETrafficLightColor';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import styles from './RenderedCode.module.less';

type FindingsPopupContentProps = {
	findings: FindingWithLayoutInfo[];
	project: string;
	commit: UnresolvedCommitDescriptor;
	lineNumber: number;
};

/** The icon and associated menu for finding(s) in code. */
export function FindingsPopup({
	findings,
	project,
	commit,
	lineNumber
}: FindingsPopupContentProps): JSX.Element | null {
	if (findings.length === 0) {
		return null;
	}

	return (
		<Popup
			hoverable
			position="right center"
			className="!max-w-[500px] !py-1 max-h-[600px] overflow-auto"
			data-testid="finding-marker-tooltip"
			trigger={<FindingMarkerArea findings={findings} lineNumber={lineNumber} />}
		>
			<FindingPopupContent findings={findings} project={project} commit={commit} />
		</Popup>
	);
}

// 2px marker width + 2px gap;
export const FINDING_MARKER_AND_GAP_WIDTH = 4;

/** The number of pixels to add as padding between the finding markers and the code. */
export const FINDING_MARKER_RIGHT_PADDING = 8;

type FindingMarkerAreaProps = ComponentProps<'div'> & {
	findings: FindingWithLayoutInfo[];
	lineNumber: number;
};

function FindingMarkerArea({ findings, lineNumber, ...divProps }: FindingMarkerAreaProps): JSX.Element {
	const flaggedCount = findings.filter(finding => FindingsUtils.isFlaggedFinding(finding.finding)).length;
	return (
		<div
			className={styles.findingMarkerArea}
			data-testid="finding-marker-area"
			data-test-finding-count={findings.length}
			data-test-flagged-count={flaggedCount}
			{...divProps}
		>
			{findings.map(finding => {
				const flaggedFinding = FindingsUtils.isFlaggedFinding(finding.finding);
				return (
					<u
						key={finding.finding.id}
						className={clsx(styles.findingMarker, {
							[styles.active!]: !flaggedFinding,
							[styles.yellow!]: finding.finding.assessment === ETrafficLightColor.YELLOW.name,
							[styles.flagged!]: flaggedFinding,
							[styles.start!]: finding.region.start === lineNumber,
							[styles.end!]: finding.region.end === lineNumber
						})}
						style={{
							left: finding.column * FINDING_MARKER_AND_GAP_WIDTH
						}}
						data-test-finding-id={finding.finding.id}
					/>
				);
			})}
		</div>
	);
}

type FindingPopupContentProps = {
	findings: FindingWithLayoutInfo[];
	project: string;
	commit: UnresolvedCommitDescriptor;
};

function FindingPopupContent({ findings, project, commit }: FindingPopupContentProps): JSX.Element {
	return (
		<ul className="list-none !ml-0 !my-2">
			{findings.map(({ finding }) => {
				return (
					<li
						key={finding.id}
						className="flex leading-relaxed [&:not(:last-child)]:mb-4 inline-code-container gap-x-2"
					>
						<Icon
							className="mr-2"
							name="warning sign"
							color={finding.assessment === ETrafficLightColor.RED.name ? 'red' : 'yellow'}
						/>
						<div>
							<TeamscaleLink
								className="text-[#333] cursor-pointer font-semibold"
								to={Links.findingDetails(project, finding.id, { commit })}
								disabled={finding.isPending}
							>
								<Markdown inline markdownText={finding.message} />
							</TeamscaleLink>
							{FindingsUtils.isFlaggedFinding(finding) ? <BlacklistInfo finding={finding} /> : null}
							<LineNumber location={finding.location} />
							{'siblingLocations' in finding &&
							finding.siblingLocations &&
							finding.siblingLocations.length > 0 ? (
								<SiblingsLinks finding={finding as TrackedFinding} project={project} commit={commit} />
							) : null}
						</div>
					</li>
				);
			})}
		</ul>
	);
}

function LineNumber({ location }: { location: ElementLocationSubtype }) {
	if (!PathUtils.isTextRegionLocation(location)) {
		return null;
	}
	return <div className="text-neutral-500">Line {PathUtils.getFormattedLineLocation(location)}</div>;
}

function SiblingsLinks<T extends TrackedFinding>({
	finding,
	project,
	commit
}: {
	finding: T;
	project: string;
	commit: UnresolvedCommitDescriptor;
}): JSX.Element {
	return (
		<div>
			Siblings:
			{finding.siblingLocations?.map((siblingLocation, index) => {
				return (
					<div key={finding.id + index} className="flex items-center space-x-1 pb-1">
						<FindingLocationLink
							project={project}
							location={siblingLocation}
							commit={commit}
							relativeToUniformPath={siblingLocation.uniformPath}
						/>
						<SiblingComparisonLink
							commit={commit}
							project={project}
							leftFindingLocation={finding.location}
							rightFindingLocation={siblingLocation}
						/>
					</div>
				);
			})}
		</div>
	);
}

function BlacklistInfo({ finding }: { finding: FindingLikeType }): JSX.Element | null {
	if (!('properties' in finding)) {
		return null;
	}
	if (finding.properties['Toleration Rationale'] != null) {
		return (
			<div className="muted">
				Marked as <span className="font-semibold">tolerated</span> by{' '}
				{finding.properties['Toleration Reporter Full Name'] ?? finding.properties['Toleration Reporter'] ?? ''}
			</div>
		);
	} else if (finding.properties['False Positive Rationale'] != null) {
		return (
			<div className="muted">
				Marked as <span className="font-semibold">false-positive</span> by{' '}
				{finding.properties['False Positive Reporter Full Name'] ??
					finding.properties['False Positive Reporter'] ??
					''}
			</div>
		);
	}
	return null;
}
